import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Icon, LatLngBounds } from 'leaflet';
import { 
  MapPin, 
  Phone, 
  Navigation, 
  Star,
  Clock,
  Loader
} from 'lucide-react';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in react-leaflet
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const HospitalMap = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([28.6139, 77.2090]); // Default: Delhi
  const [mapBounds, setMapBounds] = useState(null);

  useEffect(() => {
    getUserLocation();
  }, []);

  useEffect(() => {
    if (userLocation) {
      fetchNearbyHospitals();
    }
  }, [userLocation]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(location);
          setMapCenter([location.lat, location.lng]);
        },
        (error) => {
          console.warn('Location access denied:', error);
          // Use default location (Delhi)
          fetchNearbyHospitals();
        }
      );
    } else {
      fetchNearbyHospitals();
    }
  };

  const fetchNearbyHospitals = async () => {
    try {
      setLoading(true);
      const lat = userLocation?.lat || mapCenter[0];
      const lng = userLocation?.lng || mapCenter[1];
      
      const response = await api.get(`/hospital/nearby?lat=${lat}&lng=${lng}&radius=10000`);
      
      if (response.success) {
        setHospitals(response.hospitals);
        
        // Set map bounds to include all hospitals
        if (response.hospitals.length > 0) {
          const bounds = new LatLngBounds(
            response.hospitals.map(h => [h.location.coordinates[1], h.location.coordinates[0]])
          );
          setMapBounds(bounds);
        }
      }
    } catch (error) {
      console.error('Failed to fetch hospitals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDirections = (hospital) => {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${hospital.location.coordinates[1]},${hospital.location.coordinates[0]}`;
    window.open(url, '_blank');
  };

  const makeCall = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const createHospitalIcon = (hospital) => {
    return new Icon({
      iconUrl: 'data:image/svg+xml;base64,' + btoa(`
        <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
          <circle cx="20" cy="20" r="18" fill="#dc2626" stroke="#ffffff" stroke-width="2"/>
          <text x="20" y="25" font-family="Arial" font-size="16" font-weight="bold" fill="white" text-anchor="middle">H</text>
        </svg>
      `),
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -40],
    });
  };

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600">Finding nearby hospitals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-96 relative">
      {hospitals.length > 0 ? (
        <MapContainer
          center={mapCenter}
          zoom={userLocation ? 13 : 10}
          bounds={mapBounds}
          className="h-full w-full rounded-lg"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* User Location Marker */}
          {userLocation && (
            <Marker position={[userLocation.lat, userLocation.lng]}>
              <Popup>
                <div className="p-2">
                  <h4 className="font-semibold text-primary">Your Location</h4>
                  <p className="text-sm text-gray-600">
                    {userLocation.lat.toFixed(4)}, {userLocation.lng.toFixed(4)}
                  </p>
                </div>
              </Popup>
            </Marker>
          )}
          
          {/* Hospital Markers */}
          {hospitals.map((hospital) => (
            <Marker
              key={hospital._id}
              position={[hospital.location.coordinates[1], hospital.location.coordinates[0]]}
              icon={createHospitalIcon(hospital)}
            >
              <Popup>
                <div className="p-3 min-w-[250px]">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {hospital.name}
                  </h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {hospital.address}, {hospital.city}
                      </span>
                    </div>
                    
                    {hospital.phone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-600">{hospital.phone}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-gray-600">
                        {hospital.rating || 'N/A'} Rating
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">
                        {hospital.is24x7 ? '24/7 Available' : 'Limited Hours'}
                      </span>
                    </div>
                  </div>
                  
                  {hospital.specialties && hospital.specialties.length > 0 && (
                    <div className="mt-3">
                      <h4 className="font-medium text-gray-900 mb-1">Specialties:</h4>
                      <div className="flex flex-wrap gap-1">
                        {hospital.specialties.slice(0, 3).map((specialty, index) => (
                          <span
                            key={index}
                            className="badge-primary text-xs"
                          >
                            {specialty}
                          </span>
                        ))}
                        {hospital.specialties.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{hospital.specialties.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="flex space-x-2 mt-4">
                    <button
                      onClick={() => getDirections(hospital)}
                      className="btn-primary text-sm px-3 py-1"
                    >
                      <Navigation className="h-4 w-4 inline mr-1" />
                      Directions
                    </button>
                    {hospital.phone && (
                      <button
                        onClick={() => makeCall(hospital.phone)}
                        className="btn-secondary text-sm px-3 py-1"
                      >
                        <Phone className="h-4 w-4 inline mr-1" />
                        Call
                      </button>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      ) : (
        <div className="h-full flex items-center justify-center bg-gray-100 rounded-lg">
          <div className="text-center">
            <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Hospitals Found
            </h3>
            <p className="text-gray-600">
              Try enabling location services or search in a different area
            </p>
          </div>
        </div>
      )}
      
      {/* Hospital List */}
      {hospitals.length > 0 && (
        <div className="absolute bottom-4 left-4 right-4 bg-white rounded-lg shadow-lg p-4 max-h-48 overflow-y-auto">
          <h4 className="font-semibold text-gray-900 mb-3">Nearby Hospitals</h4>
          <div className="space-y-2">
            {hospitals.slice(0, 5).map((hospital) => (
              <div key={hospital._id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                <div className="flex-grow">
                  <h5 className="font-medium text-gray-900">{hospital.name}</h5>
                  <p className="text-sm text-gray-600">{hospital.city}</p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => getDirections(hospital)}
                    className="btn-primary text-xs px-2 py-1"
                  >
                    <Navigation className="h-3 w-3" />
                  </button>
                  {hospital.phone && (
                    <button
                      onClick={() => makeCall(hospital.phone)}
                      className="btn-secondary text-xs px-2 py-1"
                    >
                      <Phone className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalMap;
